import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert, Card, CardContent } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from 'axios';

const PaymentReports = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }

    setLoading(true);
    setError(null);
    setReportData([]);

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/payment-reports`, {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      });
      setReportData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error generating report.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Weekly Payment Reports
      </Typography>

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Select Date Range</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
              />
              <Button
                variant="contained"
                onClick={generateReport}
                disabled={loading || !startDate || !endDate}
                sx={{ alignSelf: 'flex-end' }}
              >
                Generate Report
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>
      {/* Closing tag for the date range Paper */}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      )}

      {reportData.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Report Summary</Typography>
          {reportData.map((providerReport, index) => (
            <Card key={index} elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>{providerReport.providerName}</Typography>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Service Type</TableCell>
                        <TableCell>PO Number</TableCell>
                        <TableCell align="right">Payment Amount</TableCell>
                        <TableCell align="right">Deductions</TableCell>
                        <TableCell align="right">Bonuses</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {providerReport.jobs.map((job, jobIndex) => (
                        <TableRow key={jobIndex}>
                          <TableCell>{job.date}</TableCell>
                          <TableCell>{job.time}</TableCell>
                          <TableCell>{job.serviceType}</TableCell>
                          <TableCell>{job.poNumber}</TableCell>
                          <TableCell align="right">${job.paymentAmount.toFixed(2)}</TableCell>
                          <TableCell align="right">${job.deductions.toFixed(2)}</TableCell>
                          <TableCell align="right">${job.bonuses.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={4} align="right">
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Total:</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>${providerReport.totalPayment.toFixed(2)}</Typography>
                        </TableCell>
                         <TableCell align="right">
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>${providerReport.totalDeductions.toFixed(2)}</Typography>
                        </TableCell>
                         <TableCell align="right">
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>${providerReport.totalBonuses.toFixed(2)}</Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
};
// Closing tag for the main Container

export default PaymentReports;